/**
 * Gemeinsame Aktions-Panels für die Timesheet-Freigabe (OPS-61/66).
 *
 * Inline-Panels statt Modals: erscheinen unter der jeweiligen Zeile.
 * - RejectPanel: Ablehnung nur mit Pflicht-Kategorie + Begründung (≥10 Zeichen).
 *   Kategorie `vertragsklausel` wird dem Nutzer erklärt (Stunden anerkannt,
 *   aber nicht abrechenbar — Lohn läuft, Rechnung nicht).
 * - CorrectPanel: Kunden-Korrektur (Ein-Touch) — korrigierte Stunden + Hinweis.
 * - OverridePanel: Staff-Freigabe ohne Kundenbestätigung — bewusst getrennter,
 *   visuell markierter Ausnahme-Pfad mit Pflicht-Begründung.
 */
import { TIMESHEET_REJECT_CATEGORIES, type TimesheetRejectCategory } from '@hirebotiq-ops/core';
import { useState } from 'react';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Label } from './ui/label.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.js';
import { Textarea } from './ui/textarea.js';

export const REJECT_CATEGORY_LABEL: Record<TimesheetRejectCategory, string> = {
  zeiten_fehlerhaft: 'Zeiten fehlerhaft',
  einsatz_nicht_erfolgt: 'Einsatz nicht erfolgt',
  vertragsklausel: 'Vertragsklausel (nicht abrechenbar)',
  sonstiges: 'Sonstiges',
};

const CATEGORY_HINT: Record<TimesheetRejectCategory, string> = {
  zeiten_fehlerhaft: 'Der Zettel geht zur Korrektur zurück an den Mitarbeiter.',
  einsatz_nicht_erfolgt: 'Der Fall geht zur Klärung an HireBotIQ.',
  vertragsklausel:
    'Die Stunden gelten als anerkannt (Lohn läuft), werden aber nicht in Rechnung gestellt. Bitte die Klausel benennen.',
  sonstiges: 'Der Fall geht zur Klärung an HireBotIQ.',
};

const MIN_REASON = 10;

export function RejectPanel<
  TCategory extends TimesheetRejectCategory = TimesheetRejectCategory,
>(props: {
  pending: boolean;
  categories?: readonly TCategory[];
  onSubmit: (category: TCategory, notes: string) => void;
  onCancel: () => void;
}) {
  const [category, setCategory] = useState<TCategory | ''>('');
  const [notes, setNotes] = useState('');
  const valid = category !== '' && notes.trim().length >= MIN_REASON;
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--muted)]/30 p-4 space-y-3">
      <p className="text-sm font-medium">Ablehnen — Kategorie und Begründung sind Pflicht</p>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="reject-category">Kategorie *</Label>
          <Select value={category || undefined} onValueChange={(v) => setCategory(v as TCategory)}>
            <SelectTrigger id="reject-category">
              <SelectValue placeholder="Kategorie wählen" />
            </SelectTrigger>
            <SelectContent>
              {(props.categories ?? TIMESHEET_REJECT_CATEGORIES).map((c) => (
                <SelectItem key={c} value={c}>
                  {REJECT_CATEGORY_LABEL[c]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {category && (
            <p className="text-xs text-[var(--muted-foreground)]">{CATEGORY_HINT[category]}</p>
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor="reject-notes">Begründung * (min. {MIN_REASON} Zeichen)</Label>
          <Textarea
            id="reject-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="z. B. Pause nicht abgezogen, es waren 7,5h"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={props.onCancel}>
          Abbrechen
        </Button>
        <Button
          size="sm"
          variant="destructive"
          disabled={!valid || props.pending}
          onClick={() => category && props.onSubmit(category, notes.trim())}
        >
          {props.pending ? 'Sende …' : 'Ablehnen'}
        </Button>
      </div>
    </div>
  );
}

export function CorrectPanel(props: {
  pending: boolean;
  currentHours: string | null;
  onSubmit: (correctedTotalHours: number, notes?: string) => void;
  onCancel: () => void;
}) {
  const [hours, setHours] = useState('');
  const [notes, setNotes] = useState('');
  const parsed = Number(hours.replace(',', '.'));
  const valid = hours !== '' && Number.isFinite(parsed) && parsed >= 0 && parsed <= 744;
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--muted)]/30 p-4 space-y-3">
      <p className="text-sm font-medium">Zeiten korrigieren</p>
      <p className="text-xs text-[var(--muted-foreground)]">
        Eingereicht: <span className="font-medium">{props.currentHours ?? '—'} h</span>. Ihre
        Korrektur wird dem Mitarbeiter zur Bestätigung angezeigt; der Ursprungswert bleibt
        dokumentiert.
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="correct-hours">Korrigierte Gesamtstunden *</Label>
          <Input
            id="correct-hours"
            type="number"
            step="0.25"
            min={0}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="correct-notes">Hinweis (optional)</Label>
          <Input
            id="correct-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="z. B. Pause 30 Min. abgezogen"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={props.onCancel}>
          Abbrechen
        </Button>
        <Button
          size="sm"
          disabled={!valid || props.pending}
          onClick={() => props.onSubmit(parsed, notes.trim() || undefined)}
        >
          {props.pending ? 'Sende …' : 'Korrektur senden'}
        </Button>
      </div>
    </div>
  );
}

export function OverridePanel(props: {
  pending: boolean;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= MIN_REASON;
  return (
    <div className="rounded-md border border-[var(--warning,#b45309)] bg-[var(--muted)]/30 p-4 space-y-3">
      <p className="text-sm font-medium">⚠ Freigabe ohne Kundenbestätigung (Override)</p>
      <p className="text-xs text-[var(--muted-foreground)]">
        Ausnahme-Pfad — z. B. Kunde reagiert nicht, Lohnlauf-Frist. Wird mit Begründung, Person und
        Zeitstempel revisionsfest protokolliert.
      </p>
      <div className="space-y-1">
        <Label htmlFor="override-reason">Begründung * (min. {MIN_REASON} Zeichen)</Label>
        <Textarea
          id="override-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="z. B. Kunde reagiert seit 5 Werktagen nicht, Lohnlauf-Frist 15."
        />
      </div>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={props.onCancel}>
          Abbrechen
        </Button>
        <Button
          size="sm"
          variant="destructive"
          disabled={!valid || props.pending}
          onClick={() => props.onSubmit(reason.trim())}
        >
          {props.pending ? 'Sende …' : 'Trotzdem freigeben'}
        </Button>
      </div>
    </div>
  );
}
